// Lightweight solar-position helper (no external dependency).
// Uses the NOAA low-precision algorithm to compute the sun's elevation for a
// given instant and location, which lets us decide day vs night for the
// automatic night-mode feature. Accuracy (±1 min) is far beyond what a theme
// switch needs.

const RAD = Math.PI / 180;

// Sun elevation in degrees above the horizon for a date/lat/lon.
export function solarElevationDeg(date, lat, lon) {
  const jd = date.getTime() / 86400000 + 2440587.5; // Julian date (UTC)
  const n = jd - 2451545.0; // days since J2000.0

  const L = (280.46 + 0.9856474 * n) % 360; // mean longitude (deg)
  const g = ((357.528 + 0.9856003 * n) % 360) * RAD; // mean anomaly (rad)
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD; // ecliptic longitude
  const epsilon = (23.439 - 0.0000004 * n) * RAD; // obliquity

  const delta = Math.asin(Math.sin(epsilon) * Math.sin(lambda)); // declination
  const alpha = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda)); // right ascension

  const gmstHours = (18.697374558 + 24.06570982441908 * n) % 24; // Greenwich mean sidereal time
  const lmst = (gmstHours * 15 + lon) * RAD; // local mean sidereal time (rad)
  const H = lmst - alpha; // hour angle

  const latR = lat * RAD;
  const elev = Math.asin(
    Math.sin(latR) * Math.sin(delta) + Math.cos(latR) * Math.cos(delta) * Math.cos(H)
  );
  return elev / RAD;
}

// It is "night" once the sun is below the horizon (−0.833° accounts for
// atmospheric refraction + the sun's disc radius, i.e. official sunset).
export function isNight(lat, lon, date = new Date()) {
  return solarElevationDeg(date, lat, lon) < -0.833;
}
